// Story 7.3 (ADO-1139) — the real-time availability socket layer (AC2).
//
// ONE shared `socket.io-client` connection per app session (architecture
// §real-time :760 — single instance, auth payload carries the REST JWT; the
// gateway derives the tenant from the VERIFIED token, never a client field).
// Read-only push: the client never emits domain messages — its only inputs are
// the handshake `auth` fields.
//
// Reconnect posture (story open question 2 — no visible indicator by design):
// socket.io's default reconnection covers client-side drops; on every
// (re)connect completing the room join (the gateway's `availability.subscribed`
// ack) the store re-fetches the tracked SKUs — a cheap catch-up for pushes
// missed during the gap. A SERVER-initiated disconnect (auth rejection, token
// expiry — the gateway disconnects at the verified token's `exp`) disables
// socket.io's auto-reconnect entirely, so this layer drops the dead socket and
// rebuilds it with a freshly-acquired token after a backoff. Stale/disconnected
// indicator UX is deliberately NOT built (addendum B.4, undesigned).

import { io } from 'socket.io-client';
import { currentAccessToken } from '../api/shoplink-api';
import { useAuthStore } from '../auth/useAuthStore';
import { useAvailabilityStore, type AvailabilityPush } from './useAvailabilityStore';

/** Lock-step with the gateway's emitted event names (availability.gateway.ts). */
export const AVAILABILITY_UPDATED_EVENT = 'availability.updated';
export const AVAILABILITY_SUBSCRIBED_EVENT = 'availability.subscribed';

/** The minimal socket surface the layer uses — injectable for tests. */
export interface AvailabilitySocketLike {
  on(event: string, listener: (...args: never[]) => void): unknown;
  disconnect(): unknown;
}

export type SocketFactory = (depotId: string, token: string) => AvailabilitySocketLike;

/** First rebuild delay after a server-side disconnect; doubles up to the max. */
export const INITIAL_RETRY_DELAY_MS = 5_000;
const MAX_RETRY_DELAY_MS = 60_000;

const defaultSocketFactory: SocketFactory = (depotId) =>
  io('/availability', {
    // auth as a FUNCTION: socket.io re-invokes it on every (re)connect
    // attempt, so a reconnect after token expiry carries a FRESH token — never
    // the snapshot taken at first connect (7.3 review finding).
    auth: (cb: (data: object) => void) => {
      void currentAccessToken().then(
        (token) => cb({ token: token ?? '', depotId }),
        () => cb({ token: '', depotId }),
      );
    },
    transports: ['websocket'],
  } as Parameters<typeof io>[1]) as unknown as AvailabilitySocketLike;

let activeSocket: AvailabilitySocketLike | null = null;
/** The depot the live socket is joined to — the room-change axis (7-FU-F AC4). */
let activeDepotId: string | null = null;
let connecting: Promise<void> | null = null;
/** The depot the in-flight connect is targeting (concurrent-switch guard). */
let connectingDepotId: string | null = null;
/**
 * The LATEST depot any caller has asked for — the reconciliation target for a
 * queued post-connect re-drive (7-FU-F review). Without it, an A→B→A burst that
 * all arrives during one in-flight connect ends on the STALE queued depot (B):
 * the B re-drive captures its own depot, and the later A call dedupes onto the
 * in-flight A connect without queuing a corrective re-drive, so nothing supersedes
 * the queued B. Reconciling the re-drive against this instead lands on A.
 */
let desiredDepotId: string | null = null;
/**
 * Bumped by every teardown. A connect IIFE captures it before its `await` and
 * bails if it changed by the time the token resolves — so a connect that was
 * torn down mid-flight (logout, or a switch that raced the token) can NEVER
 * publish its socket afterward (7-FU-F review R2). The `activeSocket !== null`
 * check alone did not cover this: teardown nulls `activeSocket`, so a settling
 * connect would fall through and rebuild a socket in the OLD room after logout.
 */
let socketEpoch = 0;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let retryDelayMs = INITIAL_RETRY_DELAY_MS;

/** Wire the push + catch-up listeners onto a (new) socket. Exported for tests. */
export function attachAvailabilityListeners(socket: AvailabilitySocketLike): void {
  socket.on(AVAILABILITY_UPDATED_EVENT, ((payload: AvailabilityPush) => {
    useAvailabilityStore.getState().applyPush(payload);
  }) as (...args: never[]) => void);
  // The subscribed ack fires after EVERY completed join — the initial connect
  // AND every silent reconnect — so it doubles as the catch-up trigger. The
  // initial-load fetch and this may overlap; the store's write-ordering guard
  // makes that harmless.
  socket.on(AVAILABILITY_SUBSCRIBED_EVENT, (() => {
    retryDelayMs = INITIAL_RETRY_DELAY_MS; // healthy again — reset the backoff
    void useAvailabilityStore.getState().refetchTracked();
  }) as (...args: never[]) => void);
}

/**
 * Ensure the ONE shared availability socket exists for this app session, joined
 * to `depotId`'s room. No-ops when already connected TO THE SAME DEPOT, when
 * unauthenticated (no token — never open an anonymous socket), or in a
 * test/SSR-less environment. Safe to call from every listing render.
 *
 * Story 7-FU-F (AC4): a call with a DIFFERENT depot than the live socket is a
 * live depot switch — tear the old socket down (leaving the old room) and
 * rebuild with the new `depotId` (joining `tenant:{tenantId}:depot:{depotId}`).
 * The read-only availability gateway joins the room ONLY at handshake from
 * `auth.depotId` and exposes no client-initiated room-change message, so a
 * reconnect is the only in-scope way to change rooms (D-3). 7.3's early-return
 * (`if (activeSocket !== null) return;`) ignored a changed depot — the exact bug.
 */
export async function ensureAvailabilitySocket(
  depotId: string,
  factory: SocketFactory = defaultSocketFactory,
): Promise<void> {
  // Record the latest intent up front so a post-connect re-drive reconciles to
  // it (an A→B→A burst during one in-flight connect must end on A, not B).
  desiredDepotId = depotId;
  // Already joined to this depot with a live socket — nothing to do.
  if (activeSocket !== null && activeDepotId === depotId) return;
  // Depot changed. Drop the old socket (leaves the old room) AND cancel any
  // pending old-depot reconnect, so a stale server-disconnect recovery timer
  // can't flip us back. `activeDepotId` stays set through a server-disconnect
  // gap (activeSocket null, recovery pending) — keying off it, not activeSocket,
  // catches a switch that arrives during that gap too. Falls through to rebuild
  // in the new room.
  if (activeDepotId !== null && activeDepotId !== depotId) {
    teardownActiveSocket();
  }

  if (connecting !== null) {
    // A connect for THIS depot is already in flight — dedupe onto it.
    if (connectingDepotId === depotId) return connecting;
    // A connect for a DIFFERENT depot is in flight (a switch mid-connect): let
    // it settle, then re-drive to the LATEST desired depot (read at settle time,
    // not this captured one) so a stale in-flight connect never wins and a
    // switch-back-to-the-in-flight-depot supersedes an earlier queued re-drive
    // (7-FU-F Task 3 + review — the A→B→A case).
    return connecting.then(() => {
      if (desiredDepotId !== null) return ensureAvailabilitySocket(desiredDepotId, factory);
    });
  }

  connectingDepotId = depotId;
  connecting = (async () => {
    const epoch = socketEpoch;
    try {
      const token = await currentAccessToken();
      if (token === null || token === '') return;
      // A teardown (switch / logout) since this connect began supersedes it —
      // never publish a socket the caller has already abandoned (R2).
      if (epoch !== socketEpoch) return;
      if (activeSocket !== null) return;
      const socket = factory(depotId, token);
      attachAvailabilityListeners(socket);
      attachRecoveryHandler(socket, depotId, factory);
      activeSocket = socket;
      activeDepotId = depotId;
    } catch {
      // Token acquisition rejected (e.g. a silent-refresh failure) — stay
      // disconnected; the next listing render retries. Never leak an unhandled
      // rejection out of a void-discarded call (7.3 review finding).
    } finally {
      connecting = null;
      connectingDepotId = null;
    }
  })();
  return connecting;
}

/**
 * socket.io does NOT auto-reconnect after a server-initiated disconnect
 * ("io server disconnect" — the gateway's auth-rejection / token-expiry path).
 * Without this, the dead socket pins `activeSocket` forever and real-time
 * updates silently stop for the rest of the session (7.3 review finding):
 * drop the dead socket and rebuild with a fresh token after a backoff.
 */
function attachRecoveryHandler(
  socket: AvailabilitySocketLike,
  depotId: string,
  factory: SocketFactory,
): void {
  socket.on('disconnect', ((reason: string) => {
    if (reason !== 'io server disconnect') return; // client-side drops auto-reconnect
    if (activeSocket === socket) activeSocket = null;
    scheduleAvailabilityReconnect(depotId, factory);
  }) as (...args: never[]) => void);
}

/**
 * Rebuild the socket after a backoff. Bug 1192 review — distinguish a TERMINAL
 * sign-out from a TRANSIENT token gap: `currentAccessToken()` yields null for
 * both a genuinely-signed-out user AND a momentary gap (a silent refresh in
 * flight, a brief network blip), so a single attempt that no-ops on null used
 * to end the recovery chain and silently kill real-time updates for the rest of
 * a still-authenticated session. Now: if the rebuild does not connect but the
 * auth store still reports an authenticated session, keep retrying with backoff;
 * stop only once connected (the subscribed-ack resets the backoff) or genuinely
 * signed out. Backoff is capped at `MAX_RETRY_DELAY_MS`, so a persistently
 * degraded-but-authenticated session re-probes at most once a minute.
 */
function scheduleAvailabilityReconnect(depotId: string, factory: SocketFactory): void {
  if (retryTimer !== null) return; // a retry is already pending
  retryTimer = setTimeout(() => {
    retryTimer = null;
    // A live switch (or logout) since this retry was scheduled supersedes the
    // old depot — do not reconnect (nor re-arm) a room the user has left. Only a
    // PENDING timer is cancelled by the switch teardown; a timer that already
    // fired must self-check here (7-FU-F review R2). `desiredDepotId` reflects the
    // latest intent; the reconnect's own `ensureAvailabilitySocket` would reset
    // it, so this guard MUST run before that call.
    if (depotId !== desiredDepotId) return;
    void ensureAvailabilitySocket(depotId, factory).then(() => {
      if (
        activeSocket === null &&
        useAuthStore.getState().isAuthenticated &&
        depotId === desiredDepotId
      ) {
        scheduleAvailabilityReconnect(depotId, factory);
      }
    });
  }, retryDelayMs);
  retryDelayMs = Math.min(retryDelayMs * 2, MAX_RETRY_DELAY_MS);
}

/**
 * Tear the live socket down: clear the retry timer, reset the backoff, disconnect
 * and forget it (including its depot). Shared by the depot-switch path (7-FU-F —
 * drop the old-depot socket before rebuilding on the new depot) and the public
 * logout/test teardown. A client-initiated `disconnect()` reports "io client
 * disconnect", so the recovery handler (which only acts on "io server
 * disconnect") never treats this as a drop to reconnect.
 */
function teardownActiveSocket(): void {
  if (retryTimer !== null) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
  retryDelayMs = INITIAL_RETRY_DELAY_MS;
  // Invalidate any in-flight connect so it cannot publish its socket after this
  // teardown (R2). Do NOT touch desiredDepotId here — the switch path calls this
  // between recording the new desired depot and queuing its re-drive.
  socketEpoch += 1;
  activeSocket?.disconnect();
  activeSocket = null;
  activeDepotId = null;
}

/** Tear the shared socket down (logout / tests). */
export function disconnectAvailabilitySocket(): void {
  teardownActiveSocket();
  // No depot is desired once the session ends — clears the re-drive target and
  // stops a pending server-disconnect reconnect from re-arming the old room (R2).
  desiredDepotId = null;
}
